import SEOHelmet from "@/components/SEOHelmet";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Phone, Linkedin } from "lucide-react";
import StructuredData from "@/components/StructuredData";
import { getPersonSchema } from "@/utils/seoSchemas";

const teamMembers = [
  {
    name: "Thomas Dewein",
    position: "Geschäftsführer & Cyber-Experte",
    bio: "Über 15 Jahre Erfahrung in der Versicherungsbranche mit Spezialisierung auf Cyberversicherungen für Arztpraxen und medizinische Einrichtungen.",
    email: "thomas.dewein@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Dr. Sarah Meyer",
    position: "Senior Beraterin Cyberversicherungen",
    bio: "Ehemalige IT-Sicherheitsbeauftragte mit fundiertem medizinischem Hintergrund. Expertin für DSGVO-konforme Absicherung im Gesundheitswesen.",
    email: "s.meyer@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Michael Schmidt",
    position: "Cyber-Security Spezialist",
    bio: "Zertifizierter Penetration Tester und Risikomanagement-Experte. Unterstützt Praxen bei der Schwachstellenanalyse und Prävention.",
    email: "m.schmidt@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Petra Müller",
    position: "Risikomanagement-Beraterin",
    bio: "15+ Jahre Erfahrung im Versicherungswesen mit Fokus auf Schadensprävention und Risikobewertung für medizinische Einrichtungen.",
    email: "p.mueller@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Jan Krüger",
    position: "Versicherungsberater",
    bio: "Spezialist für maßgeschneiderte Versicherungslösungen. Berät Hausärzte, Fachärzte und MVZ bei der optimalen Absicherung.",
    email: "j.krueger@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Lisa Wagner",
    position: "Kundenbetreuung & Service",
    bio: "Ihre Ansprechpartnerin für alle Fragen rund um bestehende Verträge, Schadenmeldungen und allgemeine Beratung.",
    email: "l.wagner@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Dr. Klaus Becker",
    position: "Senior Consultant & Gutachter",
    bio: "Über 25 Jahre Branchenerfahrung. Ehemaliger Praxisinhaber und zertifizierter Versicherungsgutachter für Cyber-Schäden.",
    email: "k.becker@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  },
  {
    name: "Anna Hoffmann",
    position: "IT-Sicherheitsanalystin",
    bio: "Spezialistin für Praxisverwaltungssysteme und elektronische Patientenakten. Berät zu technischen Sicherheitsmaßnahmen.",
    email: "a.hoffmann@praxis-versichert.de",
    phone: "+49 163 638 99 45",
    linkedin: "#"
  }
];

const Team = () => {
  // Generate Person schemas for all team members
  const personSchemas = teamMembers.map(member => getPersonSchema({
    name: member.name,
    position: member.position,
    email: member.email,
    phone: member.phone,
    bio: member.bio
  }));

  return (
    <>
      <SEOHelmet />
      <StructuredData data={personSchemas} />

      <Navigation />
      
      <main className="min-h-screen pt-20">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <PageBreadcrumbs 
            items={[
              { label: 'Startseite', path: '/' },
              { label: 'Unser Team' }
            ]} 
          />
          
          {/* Hero Section */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="text-center space-y-4 sm:space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                Unser Expertenteam
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Lernen Sie die Menschen kennen, die sich täglich für die Sicherheit Ihrer Praxis einsetzen. 
                Über 100 Jahre kombinierte Erfahrung in Cyberversicherung und IT-Sicherheit.
              </p>
            </div>
          </section>

          {/* Team Grid */}
          <section className="py-8 sm:py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
              {teamMembers.map((member, index) => (
                <Card 
                  key={index} 
                  className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-6 space-y-4">
                    {/* Member Info */}
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-foreground">
                        {member.name}
                      </h3>
                      <p className="text-sm font-medium text-primary">
                        {member.position}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {member.bio}
                      </p>
                    </div>

                    {/* Contact Icons */}
                    <div className="flex gap-3 pt-2">
                      <a 
                        href={`mailto:${member.email}`}
                        className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                        aria-label={`E-Mail an ${member.name}`}
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                      <a 
                        href={`tel:${member.phone}`}
                        className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                        aria-label={`Anrufen ${member.name}`}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                      <a 
                        href={member.linkedin}
                        className="p-2 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                        aria-label={`LinkedIn Profil von ${member.name}`}
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 sm:py-16 lg:py-20">
            <Card className="bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-8 sm:p-12 text-center space-y-6">
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                  Bereit für ein persönliches Gespräch?
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Kontaktieren Sie uns noch heute für eine kostenlose Erstberatung. 
                  Unser Expertenteam steht Ihnen mit Rat und Tat zur Seite.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <a 
                    href="/termin-buchen" 
                    className="inline-flex items-center justify-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200"
                  >
                    Beratungstermin vereinbaren
                  </a>
                  <a 
                    href="/kontakt" 
                    className="inline-flex items-center justify-center px-8 py-3 bg-background text-foreground border border-border rounded-lg font-semibold hover:bg-muted transition-colors duration-200"
                  >
                    Kontakt aufnehmen
                  </a>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default Team;
